import { Funnel, X, Calendar } from "@phosphor-icons/react";

const STAGES = [
  { key: "", label: "All stages" },
  { key: "prospect", label: "Prospect" },
  { key: "negotiation", label: "Negotiation" },
  { key: "proposal", label: "Proposal" },
  { key: "sales_order_created", label: "Sales Order Created" },
  { key: "closed_won", label: "Closed Won" },
  { key: "closed_lost", label: "Closed Lost" },
];

export const DATE_RANGE_OPTIONS = [
  { key: "", label: "Any date" },
  { key: "this_month", label: "This Month" },
  { key: "last_month", label: "Last Month" },
  { key: "last_3_months", label: "Last 3 Months" },
  { key: "this_quarter", label: "This Quarter" },
  { key: "this_fy", label: "This Financial Year" },
  { key: "custom", label: "Custom Range" },
];

export const DATE_BASIS_OPTIONS = [
  { key: "created_at", label: "Created Date" },
  { key: "updated_at", label: "Last Updated Date" },
  { key: "expected_close", label: "Expected Closing Date" },
  { key: "quotation_created_at", label: "Quotation Created Date" },
];

const fmtDDMMMYYYY = (iso) => {
  if (!iso || iso.length < 10) return iso || "";
  const d = new Date(`${iso.slice(0, 10)}T00:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  return `${String(d.getDate()).padStart(2, "0")} ${d.toLocaleString("en-IN", { month: "short" })} ${d.getFullYear()}`;
};

// Deals Board v2 — Filter pills row. Responsive: wraps on mobile.
export default function DealsFilters({
  filters,
  onFiltersChange,
  bdeOptions,
  managerOptions,
  onClearAll,
}) {
  const update = (key, value) => onFiltersChange({ ...filters, [key]: value });
  const updateMany = (patch) => onFiltersChange({ ...filters, ...patch });
  const dateActive = !!(filters.date_range);
  const hasActive = filters.assigned_to_id || filters.manager_id || filters.stage || dateActive;
  const dateRangeLabel = (() => {
    if (!dateActive) return "";
    if (filters.date_range === "custom") {
      const f = filters.from_date ? fmtDDMMMYYYY(filters.from_date) : "…";
      const t = filters.to_date ? fmtDDMMMYYYY(filters.to_date) : "…";
      return `${f} – ${t}`;
    }
    return DATE_RANGE_OPTIONS.find((o) => o.key === filters.date_range)?.label || filters.date_range;
  })();
  const basisLabel = DATE_BASIS_OPTIONS.find((o) => o.key === (filters.date_basis || "created_at"))?.label || "Created Date";

  const clearDateOnly = () =>
    updateMany({ date_range: "", from_date: "", to_date: "" });

  return (
    <div
      data-testid="deals-filters"
      className="bg-slate-50 border border-slate-200 px-4 py-3 mb-4 flex flex-wrap items-center gap-3"
    >
      <div className="flex items-center gap-1 text-xs uppercase tracking-[0.18em] font-bold text-slate-500">
        <Funnel size={12} /> Filter
      </div>

      <select
        data-testid="filter-assigned-bde"
        value={filters.assigned_to_id}
        onChange={(e) => update("assigned_to_id", e.target.value)}
        className="text-sm border border-slate-300 rounded-none py-2 px-2 outline-none bg-white min-w-[170px]"
      >
        <option value="">All BDEs</option>
        {bdeOptions.map((o) => (
          <option key={o.id} value={o.id}>
            {o.name} ({(o.role || "").toUpperCase()})
          </option>
        ))}
      </select>

      <select
        data-testid="filter-manager"
        value={filters.manager_id}
        onChange={(e) => update("manager_id", e.target.value)}
        className="text-sm border border-slate-300 rounded-none py-2 px-2 outline-none bg-white min-w-[170px]"
      >
        <option value="">All Managers</option>
        {managerOptions.map((o) => (
          <option key={o.id} value={o.id}>
            {o.name} ({(o.role || "").toUpperCase()})
          </option>
        ))}
      </select>

      <select
        data-testid="filter-stage"
        value={filters.stage}
        onChange={(e) => update("stage", e.target.value)}
        className="text-sm border border-slate-300 rounded-none py-2 px-2 outline-none bg-white min-w-[160px]"
      >
        {STAGES.map((s) => (
          <option key={s.key || "all"} value={s.key}>
            {s.label}
          </option>
        ))}
      </select>

      {/* Phase 2 — Date range + basis */}
      <div className="flex items-center gap-1 text-xs uppercase tracking-[0.18em] font-bold text-slate-500 pl-2 border-l border-slate-300 ml-1">
        <Calendar size={12} /> Date
      </div>
      <select
        data-testid="filter-date-range"
        value={filters.date_range || ""}
        onChange={(e) => {
          const v = e.target.value;
          if (v !== "custom") {
            updateMany({ date_range: v, from_date: "", to_date: "" });
          } else {
            update("date_range", v);
          }
        }}
        className="text-sm border border-slate-300 rounded-none py-2 px-2 outline-none bg-white min-w-[150px]"
      >
        {DATE_RANGE_OPTIONS.map((o) => (
          <option key={o.key || "any"} value={o.key}>
            {o.label}
          </option>
        ))}
      </select>
      <select
        data-testid="filter-date-basis"
        value={filters.date_basis || "created_at"}
        onChange={(e) => update("date_basis", e.target.value)}
        className="text-sm border border-slate-300 rounded-none py-2 px-2 outline-none bg-white min-w-[170px]"
      >
        {DATE_BASIS_OPTIONS.map((o) => (
          <option key={o.key} value={o.key}>
            By: {o.label}
          </option>
        ))}
      </select>
      {filters.date_range === "custom" && (
        <>
          <input
            data-testid="filter-date-from"
            type="date"
            value={filters.from_date || ""}
            onChange={(e) => update("from_date", e.target.value)}
            className="text-sm border border-slate-300 rounded-none py-2 px-2 outline-none bg-white"
            aria-label="From date"
          />
          <input
            data-testid="filter-date-to"
            type="date"
            value={filters.to_date || ""}
            onChange={(e) => update("to_date", e.target.value)}
            className="text-sm border border-slate-300 rounded-none py-2 px-2 outline-none bg-white"
            aria-label="To date"
          />
        </>
      )}

      {/* Active filter chip + clear-date-only button */}
      {dateActive && (
        <div
          data-testid="active-date-chip"
          className="inline-flex items-center gap-1 text-[11px] uppercase tracking-wider px-2 py-1 border border-[#002FA7] bg-[#002FA7]/5 text-[#002FA7] font-bold"
          title={`Filter By: ${basisLabel}`}
        >
          Filtered: {dateRangeLabel}
          <button
            data-testid="filter-clear-date"
            onClick={clearDateOnly}
            aria-label="Clear date filter"
            className="ml-1 hover:text-red-600"
          >
            <X size={12} weight="bold" />
          </button>
        </div>
      )}

      {hasActive && (
        <button
          data-testid="filter-clear-all"
          onClick={onClearAll}
          className="text-xs px-2 py-1 border border-slate-300 rounded-none uppercase tracking-wider font-bold text-slate-600 hover:bg-white inline-flex items-center gap-1"
        >
          <X size={12} /> Clear All
        </button>
      )}
    </div>
  );
}
